import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Panel, ListGrid, GhostButton } from "./ui";
import {
  formatKurus,
  DP,
  LOW_BALANCE_KURUS,
  transactionTypeLabel,
} from "./constants";
import { formatEpoch } from "../../utils/dateUtils";
import { getPersonal, getTransactions } from "../../api/digitalPayAPI";

function Stat({ label, value, tone = "primary", icon }) {
  const color = DP[tone] || DP.primary;
  return (
    <Box
      sx={{
        bgcolor: DP.surface,
        border: `1px solid ${DP.border}`,
        borderRadius: DP.radius,
        boxShadow: DP.shadowSm,
        px: 2.5,
        py: 2.25,
        position: "relative",
        overflow: "hidden",
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: 3,
          bgcolor: color,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Box>
          <Typography
            sx={{
              color: DP.textMuted,
              fontSize: "0.74rem",
              fontWeight: 650,
              letterSpacing: "0.025em",
              mb: 0.6,
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              color,
              fontSize: "1.6rem",
              fontWeight: 750,
              lineHeight: 1.15,
              letterSpacing: "-0.035em",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "12px",
            display: "grid",
            placeItems: "center",
            bgcolor: color + "14",
            color,
          }}
        >
          {icon}
        </Box>
      </Box>
    </Box>
  );
}

export default function DashboardPanel() {
  const [people, setPeople] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, t] = await Promise.all([getPersonal(), getTransactions()]);
      setPeople(p);
      setTransactions(t);
    } catch {
      // Panel boş kalır; hata detayı ilgili ekranlarda gösteriliyor.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const standardBalance = people.reduce(
      (sum, person) => sum + (Number(person.cardBalance) || 0),
      0
    );
    const isgBalance = people.reduce(
      (sum, person) => sum + (Number(person.cardIsgBalance) || 0),
      0
    );
    const lowCards = people.filter((p) => (p.cardBalance || 0) < LOW_BALANCE_KURUS).length;
    return { standardBalance, isgBalance, lowCards };
  }, [people]);

  const recent = useMemo(
    () => [...transactions].sort((a, b) => (b.epoch || 0) - (a.epoch || 0)).slice(0, 10),
    [transactions]
  );

  const columns = [
    {
      field: "epoch",
      headerName: "Tarih",
      flex: 1,
      minWidth: 150,
      valueFormatter: (v) => formatEpoch(v),
    },
    { field: "nameSurname", headerName: "Personel", flex: 1, minWidth: 140 },
    { field: "departmentName", headerName: "Departman", flex: 1, minWidth: 120 },
    { field: "productName", headerName: "Ürün", flex: 1, minWidth: 120 },
    {
      field: "transactionTypeId",
      headerName: "İşlem Tipi",
      flex: 1,
      minWidth: 150,
      valueFormatter: (value) => transactionTypeLabel(value),
    },
    {
      field: "amount",
      headerName: "Tutar",
      width: 110,
      valueFormatter: (v) => formatKurus(v),
    },
  ];

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            xl: "repeat(4, 1fr)",
          },
          gap: 2,
          mb: 3,
        }}
      >
        <Stat label="Toplam Kart" value={people.length} icon={<PeopleAltRoundedIcon />} />
        <Stat
          label="Toplam Standart Bakiye"
          value={formatKurus(stats.standardBalance) + " ₺"}
          tone="success"
          icon={<AccountBalanceWalletRoundedIcon />}
        />
        <Stat
          label="Toplam İSG Bakiye"
          value={formatKurus(stats.isgBalance) + " ₺"}
          icon={<AccountBalanceWalletRoundedIcon />}
        />
        <Stat
          label="Düşük Bakiyeli Kart"
          value={stats.lowCards}
          tone="danger"
          icon={<WarningAmberRoundedIcon />}
        />
      </Box>

      <Panel
        title="Son İşlemler"
        sub="En son 10 hareket"
        action={
          <GhostButton startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Yenile
          </GhostButton>
        }
      >
        <ListGrid
          rows={recent}
          columns={columns}
          getRowId={(r) => r.idtransaction}
          loading={loading}
          height={420}
        />
      </Panel>
    </>
  );
}
